/**
 * vmx-header.js
 * Handles:
 *   1. AnnouncementBar — rotates messages every N ms (default 4000ms).
 *   2. Header scroll detection — sticky header slides in on scroll.
 *   3. Search overlay toggle.
 *   4. Hamburger → fires vmx:drawer:open CustomEvent.
 *   5. Cart button → fires vmx:cart:opened CustomEvent.
 *   6. Cart count badge update → listens for vmx:cart:updated.
 */

(function () {
  'use strict';

  /* ── 1. Announcement Bar rotation ────────────────────────────────── */
  function initAnnouncementBar() {
    var bar = document.getElementById('vmx-announcement-bar');
    if (!bar) return;

    var messages = bar.querySelectorAll('.vmx-announcement-bar__message');
    if (messages.length < 2) return;

    var rotateMs = parseInt(bar.dataset.rotateMs, 10) || 4000;
    var current = 0;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    setInterval(function () {
      var next = (current + 1) % messages.length;

      messages[current].classList.remove('is-active');
      messages[current].classList.add('is-exit');

      var exiting = messages[current];
      setTimeout(function () {
        exiting.classList.remove('is-exit');
      }, 320);

      messages[next].classList.add('is-active');
      current = next;
    }, rotateMs);
  }

  /* ── 2. Sticky header reveal — slide in past 40% of hero ──────────
     Hero header (mint) stays in document flow and scrolls away naturally.
     White sticky header animates in once scrollY > 40% of the hero's height.
     Hero detection: [data-vmx-hero] → first <main> child → fallback viewport. */
  function initStickyHeader() {
    var sticky = document.getElementById('vmx-header-sticky');
    if (!sticky) return;

    var heroSection = (
      document.querySelector('[data-vmx-hero]') ||
      document.querySelector('main > section') ||
      document.querySelector('main > *')
    );
    var heroEl = document.getElementById('vmx-header-hero');

    function getThreshold() {
      var base = heroSection && heroSection.offsetHeight > 0
        ? heroSection.offsetHeight
        : window.innerHeight;
      // 40% of the hero section, measured from the top of that hero.
      // Add the hero header's own height so the trigger is "40% of hero
      // scrolled past" regardless of how tall the chrome above it is.
      var heroOffset = heroEl ? heroEl.offsetHeight : 0;
      return heroOffset + base * 0.4;
    }

    var threshold = getThreshold();
    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var revealed = window.scrollY > threshold;
        sticky.classList.toggle('is-visible', revealed);
        sticky.setAttribute('aria-hidden', revealed ? 'false' : 'true');
        // a11y: when hidden, also make it inert so its buttons aren't focusable
        // (an aria-hidden element must not contain focusable descendants).
        sticky.toggleAttribute('inert', !revealed);
        ticking = false;
      });
    }

    window.addEventListener('resize', function () {
      threshold = getThreshold();
      onScroll();
    }, { passive: true });

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ── 3. Search overlay — per-header (each header owns its own overlay) */
  function initSearch() {
    var toggleBtns = document.querySelectorAll('[data-vmx-search-toggle]');
    var overlays = document.querySelectorAll('[data-search-overlay]');
    if (!toggleBtns.length || !overlays.length) return;

    var openOverlay = null;

    function setOpen(overlay, value) {
      if (!overlay) return;
      if (value) {
        if (openOverlay && openOverlay !== overlay) setOpen(openOverlay, false);
        overlay.classList.add('is-open');
        overlay.setAttribute('aria-hidden', 'false');
        overlay.removeAttribute('inert');
        openOverlay = overlay;
        var input = overlay.querySelector('input[type="search"]');
        if (input) setTimeout(function () { input.focus(); }, 50);
      } else {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        overlay.setAttribute('inert', '');
        if (openOverlay === overlay) openOverlay = null;
      }
      toggleBtns.forEach(function (btn) {
        var anyOpen = !!openOverlay;
        btn.setAttribute('aria-expanded', anyOpen ? 'true' : 'false');
        btn.setAttribute('aria-label', anyOpen ? 'Close search' : 'Open search');
      });
    }

    toggleBtns.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var header = btn.closest('.vmx-header');
        var overlay = header ? header.querySelector('[data-search-overlay]') : overlays[0];
        if (!overlay) return;
        setOpen(overlay, !overlay.classList.contains('is-open'));
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openOverlay) setOpen(openOverlay, false);
    });

    document.addEventListener('click', function (e) {
      if (!openOverlay) return;
      if (!e.target.closest('.vmx-header')) setOpen(openOverlay, false);
    });
  }

  /* ── 4. Hamburger → nav drawer ───────────────────────────────────── */
  function initHamburger() {
    var hamburgers = document.querySelectorAll('[data-vmx-hamburger]');
    hamburgers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.dispatchEvent(
          new CustomEvent('vmx:drawer:open', { detail: { drawer: 'nav' } })
        );
      });
    });
  }

  /* ── 5. Cart button → vmx:cart:opened ───────────────────────────── */
  function initCartButton() {
    var cartBtns = document.querySelectorAll('[data-vmx-cart-open]');
    cartBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        document.dispatchEvent(new CustomEvent('vmx:cart:opened'));
      });
    });
  }

  /* ── 6. Cart count badge update ──────────────────────────────────── */
  function initCartCount() {
    document.addEventListener('vmx:cart:updated', function (e) {
      var count = e.detail && e.detail.item_count != null ? e.detail.item_count : 0;
      var badges = document.querySelectorAll('[data-vmx-cart-count]');
      badges.forEach(function (badge) {
        var display = count > 9 ? '9+' : String(count);
        badge.textContent = display;
        if (count === 0) {
          badge.hidden = true;
        } else {
          badge.hidden = false;
        }
      });

      var cartBtns = document.querySelectorAll('[data-vmx-cart-open]');
      cartBtns.forEach(function (btn) {
        var noun = count === 1 ? 'item' : 'items';
        btn.setAttribute('aria-label', 'Open cart (' + count + ' ' + noun + ')');
      });
    });
  }

  /* ── Boot ─────────────────────────────────────────────────────────── */
  function init() {
    initAnnouncementBar();
    initStickyHeader();
    initSearch();
    initHamburger();
    initCartButton();
    initCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
